import AssistChip from 'LuxKit/Chips/AssistChip'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import React, { ChangeEventHandler, useCallback, useState } from 'react'
import TravellersContent from './Content'
import TextInput from 'components/Common/Form/Input/TextInput'
import IconButton from 'LuxKit/Button/IconButton'
import Modal from 'LuxKit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { UseMultiRoomSelectionReturn } from 'businessTraveller/hooks/useMultiRoomSelection'

interface Props {
  onApply: () => void
  // Pass the hook to use for reading state. Must read state from hook directly (not props)
  // because modal is rendered outside the React tree, so props passed via showModal()
  // would be stale and not update when state changes.
  useSelection: () => UseMultiRoomSelectionReturn
}

function TravellersModal({ onApply, useSelection }: Props) {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const handleSearchTermChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchTerm(event.currentTarget.value)
  }, [])

  const {
    travellerEmployees,
    guestCount,
    totalTravellers,
    onAddEmployee,
    onRemoveEmployee,
    onAddGuest,
    onRemoveGuest,
  } = useSelection()

  const modalElementContext = useModalElementContext()

  const dismiss = useCallback(() => {
    setSearchTerm('')
    modalElementContext.resolve()
    onApply()
  }, [modalElementContext, onApply])

  return (
    <Modal
      mode="drawer"
      title="Who's travelling"
      subtitle={`${totalTravellers} ${totalTravellers === 1 ? 'traveller' : 'travellers'} added`}
      primaryActionText="Continue"
      onPrimaryActionClick={dismiss}
      headerExtension={<Group direction="vertical" gap={12}>
        <TextInput
          type="text"
          placeholder="Add traveller"
          onChange={handleSearchTermChange}
          value={searchTerm}
          endIcon={<>
            {!searchTerm && <LineSearchIcon />}
            {searchTerm && <IconButton
              kind="tertiary"
              horizontalOutdent="end"
              onClick={() => setSearchTerm('')}
            >
              <LineTimesIcon />
            </IconButton>}
          </>}
          noValidationSpacing
        />
        <Group direction="horizontal" gap={8} wrap="wrap">
          {travellerEmployees.map(employee => (
            <AssistChip
              onClick={() => onRemoveEmployee(employee.id)}
              key={employee.id}
              endIcon={<LineTimesIcon />}
              variant="outline"
              size="small"
            >
              {employee.firstName} {employee.lastName}
            </AssistChip>
          ))}
          {Array.from({ length: guestCount }).map((_, index) => (
            <AssistChip
              onClick={() => onRemoveGuest()}
              key={index}
              endIcon={<LineTimesIcon />}
              variant="outline"
              size="small"
            >
              Guest traveller
            </AssistChip>
          ))}
        </Group>
        <Group direction="horizontal" gap={12}>
          <TextButton
            size="medium"
            kind="tertiary"
            startIcon={<LinePlusIcon />}
            onClick={onAddGuest}
            horizontalOutdent="start"
          >
            Add guest traveller
          </TextButton>
        </Group>
      </Group>}
    >
      <TravellersContent
        searchTerm={searchTerm}
        onAddEmployee={onAddEmployee}
        onRemoveEmployee={onRemoveEmployee}
        travellerEmployees={travellerEmployees}
      />
    </Modal>
  )
}

export default TravellersModal
